import { Injectable } from '@angular/core';
import * as signalR from "@microsoft/signalr";
import { AppConfigService } from '../app-config.service';
import { BehaviorSubject } from 'rxjs';
import { AppInsightsService } from '../app-insights.service';
import { LoginService } from '../login-action/login.service';

@Injectable({
  providedIn: 'root'
})
export class DialBroadcastService {
  private hubConnection: signalR.HubConnection | undefined;
  private readonly messageSource = new BehaviorSubject<any>(null);
  public messageReceived$ = this.messageSource.asObservable();

  constructor(
    private readonly loginService: LoginService,
    private readonly appConfigService: AppConfigService,
    private readonly appInsightsService: AppInsightsService) { }

  public startConnection = async () => {
    const url = `${this.appConfigService.config?.apiConfig?.basePath}/dialhub`;
    this.hubConnection = this.buildHubConnection(url);

    this.hubConnection.onclose(error => {
      this.handleHubConnectionClose(error);
    });

    await this.startHubConnection();
  };

  public addListener = () => {
    this.registerHandlers();
  };

  public send = (type: string, message: any) => {
    const genesysUserId = localStorage.getItem('genesysUserId');
    const payload = {
      genesysUserId: genesysUserId,
      type: type,
      data: message,
    };
    this.invokeHubMethod('Send', payload, 'Sent', 'SendFailed');
  };

  private buildHubConnection(url: string): signalR.HubConnection {
    return new signalR.HubConnectionBuilder()
      .withUrl(url, { accessTokenFactory: () => this.loginService.getMicrosoftAccessToken() })
      .withAutomaticReconnect([0, 3000, 5000, 10000])
      .configureLogging(signalR.LogLevel.Debug)
      .build();
  }

  private async startHubConnection() {
    try {
      await this.hubConnection?.start();
      if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
        this.appInsightsService.logTrace(`DialBroadcastService HubConnection is connected`);
      } else {
        this.logError('DialBroadcastService HubConnection failed to connect', new Error());
      }
      this.appInsightsService.logTrace('ConnectionStarted');
      this.registerHandlers();
    } catch (err) {
      this.logError('DialBroadcastService HubConnection failed to connect', err);
      // if failed to connect, try again after 5 seconds unless the error is not recoverable
      if (
        err instanceof Error &&
        (
          err.message.includes('Invalid hub URL') ||
          err.message.includes('404') ||
          err.message.includes('401') ||
          err.message.includes('not found') ||
          err.message.includes('configuration')
        )
      ) {
        this.appInsightsService.logTrace('Non-recoverable error (Invalid URL/Configuration/Unauthorized), not retrying connection', err);
        return;
      } else {
        setTimeout(() => this.startHubConnection(), 5000);
      }
    }
  }

  public stopConnection(): void {
    if (this.hubConnection) {
      this.hubConnection.stop()
        .then(() => this.appInsightsService.logTrace('ConnectionStopped'))
        .catch(err => this.logError('Error stopping connection', err));
    }
  }

  private registerHandlers(): void {
    const messageTypes = [
      'OutboundCallReceived',
      'WaitingOnDispositionReceived',
      'SetDialpadToInitialStateReceived',
      'ToggleLocalTouchReceived',
      'SetDialpadMessageNotificationReceived',
      'ChangeCallStateReceived'
    ];

    messageTypes.forEach((messageType) => {
      this.hubConnection?.on(messageType, (message) => {
        this.handleMessage(messageType, message);
      });
    });
  }

  private handleMessage(messageType: string, message: any): void {
    this.appInsightsService.logTrace(`DialBroadcastService ${messageType} ${JSON.stringify(message)}`);
    const genesysUserId = localStorage.getItem('genesysUserId');
    if (message.genesysUserId !== genesysUserId) {
      return;
    }
    this.appInsightsService.logTrace(`${messageType} ${JSON.stringify(message)}`);
    this.messageSource.next(message);
  }

  private invokeHubMethod(method: string, payload: any, successLog: string, failureLog: string): void {
    try {
      this.hubConnection?.invoke(method, payload)
        .then(() => this.appInsightsService.logTrace(`${successLog} ${JSON.stringify(payload)}`))
        .catch(err => {
          this.logError(failureLog, err);
        });
    } catch (error) {
      this.logError('Error invoking hub method', error);
    }
  }

  private handleHubConnectionClose(error: Error | undefined): void {
    if (error) {
      this.logError('Hub connection closed with error', error);
    }
    setTimeout(() => this.startConnection(), 3000);
  }

  private logError(message: string, error: any): void {
    const newError = new Error(message);
    (newError as any).innerError = error;
    this.appInsightsService.logException(newError);
  }
}